import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import type { DefaultsSettings, Lang, TemplateSettings } from '@shared/types';

interface SettingsPayload {
  template: Record<Lang, TemplateSettings>;
  defaults: Record<Lang, DefaultsSettings>;
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const { t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<{ data: SettingsPayload }>('/settings').then((r) => r.data),
  });

  const [lang, setLang] = useState<Lang>('cs');
  const [template, setTemplate] = useState<TemplateSettings | null>(null);
  const [defaults, setDefaults] = useState<DefaultsSettings | null>(null);

  useEffect(() => {
    if (!data) return;
    setTemplate(data.template[lang]);
    setDefaults(data.defaults[lang]);
  }, [data, lang]);

  // `api` wrapper nemá put — zbytek aplikace ho nepotřebuje, tady voláme fetch přímo.
  const saveAll = useMutation({
    mutationFn: async () => {
      const put = (path: string, body: unknown) =>
        fetch(`/api${path}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }).then(async (r) => {
          if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message ?? t('settings.saveFailed'));
        });

      await put(`/settings/template/${lang}`, template);
      await put(`/settings/defaults/${lang}`, defaults);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  if (isLoading || !template || !defaults) {
    return <div className="card p-8 text-sm text-gray-500">{t('common.loading')}</div>;
  }

  const set = <K extends keyof TemplateSettings>(key: K, value: TemplateSettings[K]) =>
    setTemplate({ ...template, [key]: value });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-bold text-dark">{t('settings.title')}</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{t('settings.contentLang')}</span>
          <div className="flex gap-1 rounded-md border border-gray-200 p-1">
            {(['cs', 'en'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`rounded px-3 py-1 text-sm font-medium ${
                  lang === l ? 'bg-teal text-white' : 'text-gray-600'
                }`}
              >
                {l === 'cs' ? t('common.langCs') : t('common.langEn')}
              </button>
            ))}
          </div>
          <button className="btn-primary" onClick={() => saveAll.mutate()} disabled={saveAll.isPending}>
            {saveAll.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {saveAll.isError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {(saveAll.error as Error).message}
        </div>
      )}
      {saveAll.isSuccess && !saveAll.isPending && (
        <div className="rounded-md border border-positive/40 bg-positive-bg p-3 text-sm text-positive">
          {t('settings.saved')}
        </div>
      )}

      <section className="card space-y-3 p-5">
        <h2 className="font-heading text-lg font-semibold text-dark">{t('settings.defaults')}</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <label>
            <span className="label">{t('common.phone')}</span>
            <input
              className="input"
              value={defaults.contactPhone}
              onChange={(e) => setDefaults({ ...defaults, contactPhone: e.target.value })}
            />
          </label>
          <label>
            <span className="label">{t('common.email')}</span>
            <input
              className="input"
              value={defaults.contactEmail}
              onChange={(e) => setDefaults({ ...defaults, contactEmail: e.target.value })}
            />
          </label>
          <label>
            <span className="label">{t('common.vatRatePct')}</span>
            <input
              type="number"
              className="input"
              value={defaults.vatRate}
              onChange={(e) => setDefaults({ ...defaults, vatRate: Number(e.target.value) })}
            />
          </label>
          <label>
            <span className="label">{t('settings.validityDays')}</span>
            <input
              type="number"
              className="input"
              value={defaults.validityDays}
              onChange={(e) => setDefaults({ ...defaults, validityDays: Number(e.target.value) })}
            />
          </label>
        </div>
        <label className="block">
          <span className="label">{t('common.paymentTerms')}</span>
          <input
            className="input"
            value={defaults.paymentTerms}
            onChange={(e) => setDefaults({ ...defaults, paymentTerms: e.target.value })}
          />
        </label>
      </section>

      <section className="card space-y-3 p-5">
        <h2 className="font-heading text-lg font-semibold text-dark">{t('settings.cover')}</h2>
        <div className="grid grid-cols-3 gap-3">
          <label>
            <span className="label">{t('settings.slogan')}</span>
            <input className="input" value={template.coverSlogan} onChange={(e) => set('coverSlogan', e.target.value)} />
          </label>
          <label>
            <span className="label">{t('settings.coverTitle')}</span>
            <input className="input" value={template.coverTitle} onChange={(e) => set('coverTitle', e.target.value)} />
          </label>
          <label>
            <span className="label">{t('settings.byline')}</span>
            <input className="input" value={template.coverByline} onChange={(e) => set('coverByline', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="card space-y-3 p-5">
        <h2 className="font-heading text-lg font-semibold text-dark">{t('settings.whyUs')}</h2>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="label">{t('settings.eyebrow')}</span>
            <input className="input" value={template.whyUsEyebrow} onChange={(e) => set('whyUsEyebrow', e.target.value)} />
          </label>
          <label>
            <span className="label">Nadpis</span>
            <input className="input" value={template.whyUsTitle} onChange={(e) => set('whyUsTitle', e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="label">{t('common.lead')}</span>
          <textarea className="input" rows={3} value={template.whyUsLead} onChange={(e) => set('whyUsLead', e.target.value)} />
        </label>

        <PairList
          label={t('settings.points')}
          items={template.whyUsPoints}
          onChange={(items) => set('whyUsPoints', items)}
        />

        <div className="grid grid-cols-3 gap-3">
          <label>
            <span className="label">{t('settings.calloutTitle')}</span>
            <input
              className="input"
              value={template.whyUsCalloutTitle}
              onChange={(e) => set('whyUsCalloutTitle', e.target.value)}
            />
          </label>
          <label className="col-span-2">
            <span className="label">{t('settings.calloutBody')}</span>
            <textarea
              className="input"
              rows={2}
              value={template.whyUsCalloutBody}
              onChange={(e) => set('whyUsCalloutBody', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="card space-y-3 p-5">
        <h2 className="font-heading text-lg font-semibold text-dark">{t('settings.summaryConditions')}</h2>
        <p className="text-xs text-gray-500">
          {t('settings.summaryConditionsHint', {
            tags: '{{PLATNOST}}, {{DPH}}, {{SPLATNOST}}, {{EMAIL}}, {{TELEFON}}',
          })}
        </p>
        <textarea
          className="input font-mono text-xs"
          rows={5}
          value={template.summaryConditionsMd}
          onChange={(e) => set('summaryConditionsMd', e.target.value)}
        />
      </section>

      <section className="card space-y-3 p-5">
        <h2 className="font-heading text-lg font-semibold text-dark">{t('settings.nextSteps')}</h2>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="label">Nadpis</span>
            <input
              className="input"
              value={template.nextStepsTitle}
              onChange={(e) => set('nextStepsTitle', e.target.value)}
            />
          </label>
          <label>
            <span className="label">{t('common.lead')}</span>
            <textarea
              className="input"
              rows={2}
              value={template.nextStepsLead}
              onChange={(e) => set('nextStepsLead', e.target.value)}
            />
          </label>
        </div>

        <PairList label={t('settings.steps')} items={template.nextSteps} onChange={(items) => set('nextSteps', items)} />

        <div className="grid grid-cols-3 gap-3">
          <label>
            <span className="label">{t('settings.contactTitle')}</span>
            <input
              className="input"
              value={template.contactCalloutTitle}
              onChange={(e) => set('contactCalloutTitle', e.target.value)}
            />
          </label>
          <label className="col-span-2">
            <span className="label">{t('settings.contactBody')}</span>
            <textarea
              className="input"
              rows={2}
              value={template.contactCalloutBody}
              onChange={(e) => set('contactCalloutBody', e.target.value)}
            />
          </label>
        </div>
      </section>
    </div>
  );
}

function PairList({
  label,
  items,
  onChange,
}: {
  label: string;
  items: Array<{ title: string; body: string }>;
  onChange: (items: Array<{ title: string; body: string }>) => void;
}) {
  const { t } = useI18n();
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="label mb-0">{label}</div>
        <button className="btn-ghost text-xs" onClick={() => onChange([...items, { title: '', body: '' }])}>
          <Plus size={14} /> {t('common.add')}
        </button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 rounded-md border border-gray-200 p-2">
            <input
              className="input w-56"
              placeholder={t('common.title')}
              value={item.title}
              onChange={(e) =>
                onChange(items.map((x, ix) => (ix === i ? { ...x, title: e.target.value } : x)))
              }
            />
            <textarea
              className="input flex-1"
              rows={2}
              placeholder={t('common.text')}
              value={item.body}
              onChange={(e) =>
                onChange(items.map((x, ix) => (ix === i ? { ...x, body: e.target.value } : x)))
              }
            />
            <button
              className="text-gray-300 hover:text-danger"
              onClick={() => onChange(items.filter((_, ix) => ix !== i))}
              aria-label={t('common.remove')}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
